import { Link } from "react-router-dom";

export default function CompoundInterestExplained() {
    return (
        <>
            <p>
                Compound interest is the most powerful financial concept most people learn too late. It works in both directions:
                it builds wealth when you're the investor and costs you money when you're the borrower. Understanding the actual
                math behind it — not just the abstract idea — changes how you think about every financial decision involving
                time and interest.
            </p>

            <h2>Simple Interest vs Compound Interest: The Difference in a Year</h2>
            <p>
                Simple interest calculates earnings only on the original principal. Compound interest calculates earnings on the
                principal plus all previously accumulated interest.
            </p>
            <p>
                You invest $10,000 at 6% per year.
            </p>
            <p>
                With simple interest, you earn $600 every year. After 10 years, you have $16,000.
            </p>
            <p>
                With compound interest (compounded annually), Year 1 earns $600 (same as simple). Year 2 earns 6% on $10,600 =
                $636. Year 3 earns 6% on $11,236 = $674. And so on. After 10 years, you have $17,908 — not $16,000. That's
                $1,908 more from the same investment, with no additional contributions.
            </p>
            <p>
                After 30 years with compound interest: $57,435. With simple interest: $28,000. The gap is $29,435 — nearly
                triple the original investment — entirely from compounding.
            </p>
            <p>
                Plug your own numbers into the <Link to="/calculator/compound-interest">compound interest calculator</Link> to
                see how time, rate, and compounding frequency change the outcome.
            </p>

            <h2>Compounding Frequency Matters Too</h2>
            <p>
                Interest can compound annually, quarterly, monthly, or daily. The more frequently it compounds, the faster your
                money grows — though the difference between monthly and daily is small.
            </p>
            <p>
                $10,000 at 6% for 10 years:
            </p>
            <p>
                Compounded annually: $17,908<br />
                Compounded quarterly: $18,061<br />
                Compounded monthly: $18,194<br />
                Compounded daily: $18,221
            </p>
            <p>
                The jump from annual to monthly compounding adds $286 over 10 years on $10,000. It's meaningful but not
                dramatic. The big variable is time, not frequency.
            </p>

            <h2>The Rule of 72</h2>
            <p>
                The Rule of 72 is a quick mental math shortcut: divide 72 by the annual interest rate to estimate how many
                years it takes to double your money.
            </p>
            <p>
                At 4%: 72 ÷ 4 = 18 years to double<br />
                At 6%: 72 ÷ 6 = 12 years to double<br />
                At 8%: 72 ÷ 8 = 9 years to double<br />
                At 12%: 72 ÷ 12 = 6 years to double
            </p>
            <p>
                This is why investment return rate matters so much over long time horizons. The difference between 6% and 8%
                doesn't feel significant in year one — it's $200 on a $10,000 investment. But 6% doubles in 12 years and 8%
                doubles in 9. Over 30 years, 8% produces $100,627 vs 6%'s $57,435 on that same $10,000. That's a $43,000
                difference from a 2-point rate gap.
            </p>

            <h2>Compounding Works Against You Too</h2>
            <p>
                Everything above applies in reverse when you're the borrower. A $5,000 credit card balance at 21% APR,
                compounding monthly, doesn't just grow by $1,050 in a year — it compounds. If you make no payments:
            </p>
            <p>
                After 1 year: $6,155<br />
                After 2 years: $7,552<br />
                After 3 years: $9,265<br />
                After 5 years: $13,951
            </p>
            <p>
                The original $5,000 nearly triples in 5 years without any new spending. This is why high-interest debt is so
                dangerous — and why paying off a 21% card is the equivalent of earning a guaranteed 21% return, which you
                cannot reliably get from any investment.
            </p>

            <h2>Regular Contributions Amplify Everything</h2>
            <p>
                Compound interest is powerful on a lump sum. It's extraordinary with regular contributions.
            </p>
            <p>
                Investing $200/month at 7% annual return:
            </p>
            <p>
                After 10 years: $34,614 (you contributed $24,000 — the rest is growth)<br />
                After 20 years: $104,185 (you contributed $48,000)<br />
                After 30 years: $242,745 (you contributed $72,000)
            </p>
            <p>
                Over 30 years, your $72,000 in contributions became $242,745. That's $170,745 in compound growth. You didn't
                earn that — time and interest earned it for you.
            </p>

            <h2>What Inflation Does to the Picture</h2>
            <p>
                If inflation averages 3% per year, a "real" (inflation-adjusted) 7% return is actually a 4% real return. This
                matters for retirement planning, where purchasing power is what you're trying to preserve. Still, even at a 4%
                real return, the math above shows that consistent investing over long periods produces significant real wealth.
            </p>

            <h2>The Bottom Line</h2>
            <p>
                Compound interest rewards patience and punishes inaction. Start early, contribute consistently, and let time do
                the heavy lifting. The difference between starting at 25 and 35 isn't 10 years of contributions — it's 10 years
                of compounding on top of your balance, which can be worth more than all the contributions you ever make after
                35. Model your own numbers using the{" "}
                <Link to="/calculator/compound-interest">compound interest calculator</Link>.
            </p>
        </>
    );
}